import { Building2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Bank } from "@/apis/gql/types";
import { DatePickerInput } from "@/components/shared/form/date-picker-input";

import { SwitchInput } from "@/components/shared/form/switch-input";
import { TextInput } from "@/components/shared/form/text-input";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import BankCard from "./bank-card";

type Props = {
  bank: Bank;
  txnImportEnabled: boolean;
  startImportDate?: Date;
  connectionName: string;
  bankAlreadyConnected?: boolean;
  setTxnImportEnabled: (txnImportEnabled: boolean) => void;
  setStartImportDate: (startImportDate: Date | undefined) => void;
  setConnectionName: (name: string) => void;
  isInterfaceSelectionEnabled?: boolean;
  selectedInterface?: "FINTS_SERVER" | "XS2A" | null;
  setSelectedInterface?: (interface_type: "FINTS_SERVER" | "XS2A") => void;
};

export const ConnectionStepImportSettings = ({
  bank,
  txnImportEnabled,
  startImportDate,
  connectionName,
  bankAlreadyConnected = false,
  setTxnImportEnabled,
  setStartImportDate,
  setConnectionName,
  isInterfaceSelectionEnabled = false,
  selectedInterface = null,
  setSelectedInterface,
}: Props) => {
  const { t } = useTranslation();

  const schema = z.object({
    connectionName: bankAlreadyConnected
      ? z.preprocess(
          (v) => v ?? "",
          z
            .string()
            .min(1, { message: t("required") })
            .max(19),
        )
      : z.preprocess((v) => v ?? "", z.string().max(19)),
    txnImportEnabled: z.boolean(),
    startImportDate: z.date().optional(),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    values: {
      connectionName,
      txnImportEnabled: bankAlreadyConnected ? false : txnImportEnabled,
      startImportDate: bankAlreadyConnected ? undefined : startImportDate,
    },
    mode: "onChange",
  });

  useEffect(() => {
    const subscription = form.watch((values) => {
      const v = values as unknown as {
        connectionName?: string;
        txnImportEnabled?: boolean;
        startImportDate?: Date;
      };
      setConnectionName(v.connectionName ?? "");
      setTxnImportEnabled(bankAlreadyConnected ? false : !!v.txnImportEnabled);
      setStartImportDate(bankAlreadyConnected ? undefined : v.startImportDate);
    });
    return () => subscription.unsubscribe();
  }, [
    form,
    bankAlreadyConnected,
    setConnectionName,
    setTxnImportEnabled,
    setStartImportDate,
  ]);

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          {t("bank_connections.import_settings.title")}
        </DialogTitle>
      </DialogHeader>
      <BankCard
        bank={bank}
        onSelect={() => {}}
        disabled={false}
        nonSelectView={true}
      />

      <Separator />

      <Form {...form}>
        <div className="space-y-6">
          <div className="space-y-6">
            {isInterfaceSelectionEnabled && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  {t("bank_connection.select_interface")}
                </Label>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("bank_connection.select_interface_description")}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setSelectedInterface?.("FINTS_SERVER")}
                    className="rounded-lg border text-card-foreground shadow-sm h-20 px-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 bg-white border-gray-200"
                  >
                    <div className="flex items-center justify-center gap-3 h-full">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedInterface === "FINTS_SERVER"
                            ? "bg-black border-black"
                            : "border-gray-400"
                        }`}
                      >
                        {selectedInterface === "FINTS_SERVER" && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">FinTS</span>
                        <span className="text-xs text-gray-500">
                          {t("bank_connection.fints_description")}
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedInterface?.("XS2A")}
                    className="rounded-lg border text-card-foreground shadow-sm h-20 px-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 bg-white border-gray-200"
                  >
                    <div className="flex items-center justify-center gap-3 h-full">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedInterface === "XS2A"
                            ? "bg-black border-black"
                            : "border-gray-400"
                        }`}
                      >
                        {selectedInterface === "XS2A" && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">
                          {t("bank_connection.xs2a")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {t("bank_connection.xs2a_description")}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {t("bank_connections.import_settings.connection_name")}
              </Label>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(
                  "bank_connections.import_settings.connection_name_description",
                )}
              </p>
              <div className="w-full">
                <TextInput
                  name="connectionName"
                  maxLength={19}
                  placeholder="z. B. Nutzerkonto"
                />
              </div>
            </div>

            <div className="flex items-center justify-between space-x-4">
              <div className="space-y-2 max-w-[70%]">
                <Label
                  className={`text-base font-semibold ${
                    bankAlreadyConnected ? "text-muted-foreground" : ""
                  }`}
                >
                  {t("bank_connections.import_settings.txn_import_label")}
                </Label>
                <p
                  className={`text-sm text-muted-foreground leading-relaxed ${
                    bankAlreadyConnected ? "opacity-70" : ""
                  }`}
                >
                  {t("bank_connections.import_settings.txn_import_description")}
                </p>
              </div>
              <SwitchInput
                name="txnImportEnabled"
                onCheckedChange={
                  bankAlreadyConnected ? undefined : setTxnImportEnabled
                }
                disabled={bankAlreadyConnected}
              />
            </div>

            {form.watch("txnImportEnabled") && (
              <div className="space-y-2">
                <Label
                  className={`text-base font-semibold ${
                    bankAlreadyConnected ? "text-muted-foreground" : ""
                  }`}
                >
                  {t(
                    "bank_connections.import_settings.start_import_date_label",
                  )}
                </Label>
                <p
                  className={`text-sm text-muted-foreground ${
                    bankAlreadyConnected ? "opacity-70" : ""
                  }`}
                >
                  {t(
                    "bank_connections.import_settings.start_import_date_description",
                  )}
                </p>
                <div className="relative z-[60] w-1/2">
                  <DatePickerInput
                    name="startImportDate"
                    disabled={bankAlreadyConnected}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Form>
    </div>
  );
};
